import { applyMode, Mode } from '@amzn/awsui-global-styles';
import { faChevronDown, faChevronUp, faExternalLinkAlt, faMoon, faSun } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useMatch, useLocation } from 'react-router-dom';
import { eCacheKeys, eLocalStorageKeys, generateProfilePictureUrl } from 'src/constants/AppConstants';
import { useAskQuick } from 'src/components/ask-quick/AskQuickContext';
import { PARENT_TO_EMBED_MESSAGES } from 'src/constants/EmbedMessageConstants';
import { isEmbedReadyMessage, postMessageToEmbeddedIframe } from 'src/components/das-finsuite/embedded-apps/utils/EmbeddingUtils';
import { usePersistedState } from 'src/hooks/useLocalStorage';
import DFP_HEADER_LOGO from 'src/images/FinSuite-logo.png';
import QUICKSUITE_LOGO_DEFAULT from 'src/images/QuickSuite-logo-default.svg';
import QUICKSUITE_LOGO_ACTIVE from 'src/images/QuickSuite-logo-active.svg';
import { useAppContext } from '../context/AppContextProvider';
import { useAuth } from '../context/AuthContextProvider';
import './DFPTopNavigation.scss';
import { GlobalSearchBar } from './GlobalSearchBar';
import { PROFILE_DROPDOWN_ITEMS } from './ProfileDropdownConstants';
import { ROUTE_CONFIG } from 'src/utilities/RouteConfig';
import { DFPProfileComponent } from './DFPProfileComponent';
import { getEmployeeByLogin } from 'src/api/papi-service';
import { mapEmployeeToProfileInfo } from 'src/utilities/profile-utils';

export const DFPTopNavigation: React.FC = () => {
  const navigate = useNavigate();
  const { Alias: userAlias } = useAuth();
  const { parsedApplications } = useAppContext();
  const { togglePanel: toggleAskQuick, isOpen: isAskQuickOpen } = useAskQuick();
  const [isDarkModeEnabled, setIsDarkModeEnabled] = usePersistedState(eLocalStorageKeys.IsDarkModeEnabled, false);
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const profilePanelRef = useRef<HTMLDivElement>(null);

  const embedMatch = useMatch('/all-apps/:id');
  const { pathname } = useLocation();

  // Tracks whether the currently embedded app supports postMessage navigation (handshake protocol)
  const embedReadyRef = useRef(false);

  // Listen for FINSUITE_EMBED_READY from the embedded iframe.
  // When received, we know the app supports postMessage navigation and can avoid remounting.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (isEmbedReadyMessage(event)) {
        embedReadyRef.current = true;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Reset readiness when navigating away from an embedded app
  useEffect(() => {
    if (!embedMatch?.params?.id) {
      embedReadyRef.current = false;
    }
  }, [embedMatch?.params?.id]);

  useEffect(() => {
    applyMode(isDarkModeEnabled ? Mode.Dark : Mode.Light);
  }, [isDarkModeEnabled]);

  // Pre-warm profile info cache on page load
  useEffect(() => {
    const prewarmProfileCache = async () => {
      // Skip if already cached
      if (sessionStorage.getItem(eCacheKeys.UserProfileInfo)) return;

      try {
        const response = await getEmployeeByLogin(userAlias, 'job');
        if (!response.employee) return;

        const info = mapEmployeeToProfileInfo(response.employee);
        sessionStorage.setItem(eCacheKeys.UserProfileInfo, JSON.stringify(info));
      } catch {
        // Best-effort pre-warm; DFPProfileComponent has its own fallback fetch on cache miss
      }
    };

    prewarmProfileCache();
  }, [userAlias]);

  // Close profile panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profilePanelRef.current && !profilePanelRef.current.contains(event.target as Node)) {
        setShowProfilePanel(false);
      }
    };

    if (showProfilePanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfilePanel]);

  const embeddedApp = useMemo(() => {
    if (!embedMatch?.params?.id) return null;
    const app = parsedApplications.filter((a) => a.isActive).find((a) => a.id === embedMatch.params.id);
    if (!app || !app.embeddingEnabled) return null;
    return {
      url: app.applicationLink || null,
      name: app.applicationName || null
    };
  }, [embedMatch, parsedApplications]);

  /**
   * Sends a postMessage to the embedded iframe if it supports the protocol.
   * Returns true if the message was sent, false otherwise.
   */
  const sendMessageToEmbed = useCallback((type: string, payload?: Record<string, unknown>): boolean => {
    if (!embedReadyRef.current) return false;
    return postMessageToEmbeddedIframe(type, payload);
  }, []);

  const handleEmbeddedAppHomeNavigation = useCallback(() => {
    if (sendMessageToEmbed(PARENT_TO_EMBED_MESSAGES.NAVIGATE_HOME)) return;
    // Fallback: remount the iframe (for apps that don't implement the protocol)
    if (embedMatch?.params?.id) {
      navigate(`/all-apps/${embedMatch.params.id}?t=${Date.now()}`);
    }
  }, [embedMatch?.params?.id, navigate, sendMessageToEmbed]);

  const handleDarkModeToggle = useCallback(() => {
    const newDarkMode = !isDarkModeEnabled;
    setIsDarkModeEnabled(newDarkMode);
    sendMessageToEmbed(PARENT_TO_EMBED_MESSAGES.THEME_CHANGE, { isDarkMode: newDarkMode });
  }, [isDarkModeEnabled, setIsDarkModeEnabled, sendMessageToEmbed]);

  const routeConfig = ROUTE_CONFIG[pathname];
  const displayAppName =
    embeddedApp?.name?.toLowerCase() ?? (routeConfig?.navigationSection === 'org-tools' ? routeConfig.navigationText?.toLowerCase() : null) ?? null;

  return (
    <nav className="dfp-top-nav" aria-label="Main navigation">
      <div className="dfp-top-nav__left">
        <a
          href="/"
          className="dfp-top-nav__logo-link"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
          aria-label="FinSuite Home"
        >
          <img src={DFP_HEADER_LOGO} alt="FinSuite Logo" className="dfp-top-nav__logo" />
        </a>
        {displayAppName && (
          <>
            <span className="dfp-top-nav__divider" />
            {embeddedApp && embedMatch?.params?.id ? (
              <a
                href={`/all-apps/${embedMatch.params.id}`}
                className="dfp-top-nav__app_text"
                onClick={(e) => {
                  e.preventDefault();
                  handleEmbeddedAppHomeNavigation();
                }}
                aria-label={`Navigate to ${embeddedApp.name} home`}
              >
                {displayAppName}
              </a>
            ) : routeConfig?.path ? (
              <a href={routeConfig.path} className="dfp-top-nav__app_text" aria-label={`Navigate to ${routeConfig.navigationText} home`}>
                {displayAppName}
              </a>
            ) : (
              <div className="dfp-top-nav__app_text">{displayAppName}</div>
            )}
          </>
        )}
      </div>
      <div className="dfp-top-nav__center">
        <GlobalSearchBar />
      </div>
      <div className="dfp-top-nav__right">
        {embeddedApp?.url && (
          <button
            className="dfp-top-nav__utility-btn"
            onClick={() => embeddedApp.url && window.open(embeddedApp.url, '_blank', 'noopener,noreferrer')}
            aria-label={`Open ${embeddedApp.name || 'application'} in a new tab`}
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </button>
        )}
        <button
          className="dfp-top-nav__utility-btn"
          onClick={handleDarkModeToggle}
          aria-label={`Switch to ${isDarkModeEnabled ? 'light' : 'dark'} mode`}
        >
          <FontAwesomeIcon icon={isDarkModeEnabled ? faMoon : faSun} />
        </button>
        <div className="dfp-top-nav__divider-v" />
        <button
          className="dfp-top-nav__utility-btn"
          onClick={toggleAskQuick}
          aria-label="Toggle QuickSuite panel"
          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <img src={isAskQuickOpen ? QUICKSUITE_LOGO_ACTIVE : QUICKSUITE_LOGO_DEFAULT} alt="QuickSuite" className="dfp-top-nav__quicksuite-logo" />
          {/* TODO: Add following code when AskRino Added to Project: */}
          {/* <FontAwesomeIcon icon={isAskQuickOpen ? faChevronUp : faChevronDown} className="avatar-dropdown__chevron" /> */}
        </button>
        <div className="dfp-top-nav__divider-v" />
        <div className="avatar-dropdown" ref={profilePanelRef}>
          <button
            className="avatar-dropdown__button"
            onClick={() => setShowProfilePanel((prev) => !prev)}
            aria-expanded={showProfilePanel}
            aria-label="Toggle profile panel"
          >
            <img src={generateProfilePictureUrl(userAlias)} alt={userAlias} className="avatar-dropdown__image" />
            <FontAwesomeIcon icon={showProfilePanel ? faChevronUp : faChevronDown} className="avatar-dropdown__chevron" />
          </button>
          {showProfilePanel && (
            <div className="profile-panel">
              <DFPProfileComponent profile_items={PROFILE_DROPDOWN_ITEMS} onCancel={() => setShowProfilePanel(false)} />
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};
